import { ToastrService } from 'ngx-toastr';
import { Subject, catchError, of, take, takeUntil } from 'rxjs';
import { Component, ChangeDetectorRef, OnDestroy, Renderer2, Inject, PLATFORM_ID } from '@angular/core';

import { AppService, AuthService, SeoService, SharedService } from '../../services';
import { InternalProductType } from '@sifca-monorepo/ecommerce-generator';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { identity, map, pickBy } from 'lodash';
import { OrdersService } from '../../services/order/order/order.service';

@Component({
  template: '',
})
export class BaseWishlistComponent implements OnDestroy {
  private unsubscribeAll: Subject<any> = new Subject<any>();

  seo: any;
  isOutOfStock: boolean;
  authenticated: boolean;
  isButtonDisabled = false;
  products: InternalProductType[];

  constructor(
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected appService: AppService,
    protected titleTagService: Title,
    protected seoService: SeoService,
    protected authService: AuthService,
    protected orderService: OrdersService,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.seoService
        .getGeneralSEOJSON()
        .pipe(
          take(1),
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((res) => {
          this.seo = res;
          if (this.seo?.title) {
            this.titleTagService.setTitle(this.seo?.title);
          }
          this.sharedService.tags$ = [
            ...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))),
            { charset: 'UTF-8' },
          ];
            this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
              const link = this.renderer.createElement('link');
              if (seoLink.sizes) {
                link.sizes = seoLink.sizes;
              }
              if (seoLink.href) {
                link.href = seoLink.href;
              }
              if (seoLink.rel) {
                link.rel = seoLink.rel;
              }
              this.renderer.appendChild(this.document.head, link);
              return link;
            });
        });
    this.authService.authenticated$.pipe(takeUntil(this.unsubscribeAll)).subscribe((authenticated: boolean) => {
      this.authenticated = authenticated;
      this.appService.favoriteProducts$.subscribe((products: InternalProductType[]) => {
        this.products = products;
        this.changeDetectorRef.markForCheck();
      });
      this.changeDetectorRef.markForCheck();
    });
  }

  removeProductFromFavorites(productId: string) {
    this.appService.removeSimpleProductFromFavorites(productId)
    .pipe(
      catchError(() => {
        this.toastrService.error("Quelque chose s'est mal passé");
        this.changeDetectorRef.markForCheck();
        return of(null);
      }),
    )
    .subscribe((res) => {
      if (res) {
        this.toastrService.success("Produit retiré de la wishlist.");
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  addToCart(product: InternalProductType) {
    this.isButtonDisabled = true;
    this.appService
      .getStockByTargetAndBarcode(product.barcodes[0].id)
      .pipe(
        catchError(() => {
          this.isOutOfStock = true;
          this.toastrService.error('Le produit est en rupture de stock !');
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.isOutOfStock = res.outOfStock;
          if (this.isOutOfStock === true) {
            this.toastrService.error('Le produit est en rupture de stock !');
          } else {
            if (!this.authenticated) {
              if (this.appService.itemInCart(product.barcodes[0])) {
                const value: any = {
                  barcode: product.barcodes[0],
                  price: {
                    unitGross: product.barcodes[0].price,
                    gross: product.barcodes[0].price,
                  },
                  product,
                  quantity: 1,
                };
                this.appService.updateLocalCart(value);
              } else {
                const value: any = {
                  barcode: product.barcodes[0],
                  price: {
                    unitGross: product.barcodes[0].price,
                    gross: product.barcodes[0].price,
                  },
                  product,
                  quantity: 1,
                };
                this.appService.addToLocalCart(value);
              }
              this.toastrService.success('Produit ajouté au panier.');
              this.isButtonDisabled = false;
            } else {
              const formValue = {
                barcode: product.barcodes[0].id,
                quantity: 1,
              };
              this.orderService.addProductsToCart([formValue])
              .pipe(
                catchError(() => {
                   this.toastrService.error("Quelque chose s'est mal passé !");
                  return of(null);
                }),
              )
              .subscribe((res) => {
                if (res) {
                  this.toastrService.success('Produit ajouté au panier.');
                  this.isButtonDisabled = false;
                }
              });
              this.appService.loadCart();
            }
          }
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
